"use client";

import React from "react";
import { AlertTriangle } from "lucide-react";
import { trackError, submitFeedback } from "@/lib/analytics";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional fallback override. If not provided, uses the default fallback. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches React render errors anywhere below it in the tree.
 * Logs the error to /api/events via analytics.trackError, then shows a
 * friendly fallback UI with an optional "Send details" feedback box.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to analytics pipeline (fire-and-forget)
    trackError(error, {
      source: "ErrorBoundary",
      component_stack: info.componentStack?.slice(0, 2000),
    });
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <ErrorFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ */
/*  Default fallback UI                                                */
/* ------------------------------------------------------------------ */

function ErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  async function sendDetails() {
    setSending(true);
    await submitFeedback({
      kind: "error",
      message: message.trim() || undefined,
      context: {
        error_message: error.message.slice(0, 500),
        stack: error.stack?.slice(0, 2000),
        url: typeof window !== "undefined" ? window.location.href : undefined,
      },
    });
    setSending(false);
    setSent(true);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
        <AlertTriangle className="w-6 h-6 text-amber-500 mb-3" strokeWidth={1.75} aria-hidden="true" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
          Something broke
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          We&apos;ve logged the error. Want to tell us what you were doing? (Optional)
        </p>

        {sent ? (
          <div className="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 mb-4">
            Thanks — we&apos;ll look into it.
          </div>
        ) : (
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What were you doing when this happened?"
            rows={3}
            className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 mb-4 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              reset();
              if (typeof window !== "undefined") window.location.reload();
            }}
            className="flex-1 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Reload page
          </button>
          {!sent && (
            <button
              onClick={sendDetails}
              disabled={sending}
              className="flex-1 text-sm bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {sending ? "Sending…" : "Send details"}
            </button>
          )}
        </div>

        {process.env.NODE_ENV === "development" && (
          <details className="mt-4 text-xs text-gray-400">
            <summary className="cursor-pointer">Technical details (dev only)</summary>
            <pre className="mt-2 overflow-auto max-h-40 p-2 bg-gray-100 dark:bg-gray-800 rounded">
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
